import { useMutation } from "@tanstack/react-query";
import {
    getAuth,
    signInWithEmailAndPassword,
    getMultiFactorResolver,
    TotpMultiFactorGenerator,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const loginUser = async ({ data, navigate }) => {
    const auth = getAuth();

    try {
        await signInWithEmailAndPassword(auth, data.email, data.password);
    } catch (err) {
        if (err.code === "auth/multi-factor-auth-required") {
            return {
                mfaRequired: true,
                resolver: getMultiFactorResolver(auth, err),
            };
        }

        if (err.code === "auth/user-disabled") {
            toast.error("This account is currently disabled");
        } else if (err.code === "auth/password-does-not-meet-requirements") {
            toast.error(
                "Your password no longer meets our security requirements. Please reset your password and try again.",
            );
        } else if (err.code === "auth/invalid-credential") {
            toast.error(
                "Please check your email address or password and try again.",
            );
        } else {
            toast.error("Something went wrong. Please try again.");
        }

        throw err;
    }

    const user = auth.currentUser;

    await user.reload();

    if (!user.emailVerified) {
        navigate("/verify-email", { replace: true });
    }

    return { mfaRequired: false };
};

export const useLogin = () => {
    const navigate = useNavigate();

    return useMutation({
        mutationFn: (data) => loginUser({ data, navigate }),
        retry: false,
    });
};

// Verify the authenticator code
const verifyLoginMfa = async ({ resolver, code }) => {
    const totpHint = resolver.hints.find(
        (hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID,
    );

    if (!totpHint) {
        throw new Error("No authenticator app factor found");
    }

    const assertion = TotpMultiFactorGenerator.assertionForSignIn(
        totpHint.uid,
        code,
    );

    return resolver.resolveSignIn(assertion);
};

export const useVerifyLoginMfa = () => {
    const navigate = useNavigate();

    return useMutation({
        mutationFn: verifyLoginMfa,
        retry: false,

        onSuccess: (userCredential) => {
            const user = userCredential.user;

            if (!user.emailVerified) {
                navigate("/verify-email", { replace: true });
            }
        },

        onError: (err) => {
            if (err.code === "auth/invalid-verification-code") {
                toast.error("That code is incorrect");
            } else {
                toast.error("Error verifying authentication code");
            }
        },
    });
};
